/**
 * Sepet / checkout kargo (TRY) — paneldeki ürün kargo alanları + useShipping varsayılanları.
 * Eski sabit 259 TL bandı kaldırıldı.
 */
import { calculateShippingCost } from '@/hooks/useShipping';
import { normalizeCountryCode } from '@/lib/country-i18n';

export function isTurkeyDestination(country) {
  return normalizeCountryCode(country) === 'TR';
}

/**
 * Sepet ve checkout aynı teslimat ülkesi tahminini kullanmalı.
 * Adres seçildiyse o geçerli; yoksa TRY → TR, değilse algılanan ülke.
 */
export function guessShippingDestination({ addressCountry, currency, countryCode } = {}) {
  const fromAddress = normalizeCountryCode(addressCountry);
  if (fromAddress) return fromAddress;
  if (currency === 'TRY') return 'TR';
  const fromGeo = normalizeCountryCode(countryCode);
  if (fromGeo) return fromGeo;
  if (currency === 'EUR') return 'DE';
  if (currency === 'USD') return 'US';
  if (currency === 'GBP') return 'GB';
  return 'DE';
}

function isTurkey(country) {
  return isTurkeyDestination(country);
}

/**
 * Ürünlerde kayıtlı sabit kargo (TRY) — yurtiçi: shipping_cost, yurtdışı: international_shipping_cost
 * Hiçbir üründe alan yoksa null (varsayılan tablo kullanılır).
 * Yurtiçi: 0 = satıcı ücretsiz kargo.
 * Yurtdışı: 0 çoğu üründe "boş/default" olarak kayıtlı — ücretsiz sayılmaz (null gibi davranır).
 * Gerçek ücretsiz yurtdışı için free_international_shipping / international_shipping_free bayrağı gerekir.
 */
export function getMaxProductShippingTRY(items, domestic) {
  const key = domestic ? 'shipping_cost' : 'international_shipping_cost';
  const perItem = (items || []).map((i) => i?.product?.[key]);
  const hasExplicit = perItem.some(
    (v) => v !== undefined && v !== null && v !== '',
  );
  if (!hasExplicit) return null;
  const vals = perItem
    .filter((v) => v !== undefined && v !== null && v !== '')
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  if (!vals.length) return null;
  const max = Math.max(...vals);

  if (!domestic && max <= 0) {
    const freeFlag = (items || []).some((i) => {
      const p = i?.product;
      return !!(
        p?.free_international_shipping
        || p?.international_shipping_free
        || p?.free_shipping_international
      );
    });
    if (!freeFlag) return null; // varsayılan yurtdışı tarifeye düş
  }
  return max;
}

export function subtotalToTRY(amount, currency, exchangeRates) {
  if (!amount) return 0;
  if (!currency || currency === 'TRY') return amount;
  const rate = exchangeRates?.[currency];
  if (!rate || rate <= 0) return amount;
  return amount / rate;
}

/**
 * @param {object} opts
 * @param {Array} opts.items — sepet satırları (product dolu)
 * @param {string} opts.destinationCountry — teslimat ülkesi (TR / DE / …)
 * @param {number} opts.subtotalTRY — ara toplam TRY (ücretsiz kargo eşiği için)
 * @param {number} opts.totalWeightKg
 */
export function getCartShippingTRY({
  items,
  destinationCountry,
  subtotalTRY,
  totalWeightKg,
}) {
  const domestic = isTurkey(destinationCountry);

  const freeProbe = calculateShippingCost({
    destinationCountry,
    cartSubtotal: subtotalTRY,
    totalWeight: 1,
    shippingType: 'standard',
  });
  if (freeProbe.isFree) {
    return 0;
  }

  const fromProducts = getMaxProductShippingTRY(items, domestic);
  const weightExtra =
    totalWeightKg > 1 ? (totalWeightKg - 1) * (domestic ? 15 : 35) : 0;

  if (fromProducts !== null) {
    // Yalnızca yurtiçi 0 = ücretsiz; yurtdışı 0 yukarıda null'a çevrildi
    if (fromProducts === 0) {
      return 0;
    }
    return Math.round((fromProducts + weightExtra) * 100) / 100;
  }

  const calc = calculateShippingCost({
    destinationCountry: destinationCountry || (domestic ? 'TR' : 'DE'),
    cartSubtotal: subtotalTRY,
    totalWeight: totalWeightKg,
    shippingType: 'standard',
  });
  // Güvenlik: yurtdışında hesap 0 dönerse taban ücret
  if (!domestic && (!calc.cost || calc.cost <= 0) && !calc.isFree) {
    return 250;
  }
  return calc.cost;
}
